import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Search,
  RefreshCw,
  Filter,
  X,
  Clock,
  User,
  Bot,
  Zap,
  Eye,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  ScrollText,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ActionLog {
  id: string;
  created_at: string;
  actor_type: string;
  actor_id: string | null;
  actor_name: string | null;
  entity_type: string;
  entity_id: string | null;
  action: string;
  platform: string | null;
  status: string;
  details: Record<string, unknown>;
  source: string;
}

interface WhatsAppLog {
  id: string;
  type: string;
  status: 'success' | 'error' | 'info';
  message: string;
  error?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
}

const ITEMS_PER_PAGE = 50;

export const Logs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'actions' | 'whatsapp'>('actions');
  const [logs, setLogs] = useState<ActionLog[]>([]);
  const [whatsappLogs, setWhatsappLogs] = useState<WhatsAppLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('add_creative');
  const [whatsappStatusFilter, setWhatsappStatusFilter] = useState<string>('all');
  const [whatsappTypeFilter, setWhatsappTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // entity_id (creative id) → { video_name, release_name } looked up from ad_creatives + releases
  const [creativeMap, setCreativeMap] = useState<Record<string, { video_name: string | null; release_name: string | null }>>({});

  const fetchLogs = useCallback(async () => {
    if (activeTab === 'whatsapp') {
      try {
        setLoading(true);
        let query = supabase
          .from('whatsapp_logs')
          .select('*', { count: 'exact' })
          .order('created_at', { ascending: false });

        if (whatsappStatusFilter !== 'all') {
          query = query.eq('status', whatsappStatusFilter);
        }
        if (whatsappTypeFilter !== 'all') {
          query = query.eq('type', whatsappTypeFilter);
        }

        // When searching, load more results to filter client-side
        // (details/metadata are JSONB and can't be easily searched server-side)
        const limit = searchTerm ? 500 : ITEMS_PER_PAGE;
        const offset = searchTerm ? 0 : page * ITEMS_PER_PAGE;
        query = query.range(offset, offset + limit - 1);

        const { data, error: fetchError, count } = await query;
        if (fetchError) throw fetchError;
        const allData = (data as unknown as WhatsAppLog[]) || [];
        setWhatsappLogs(allData);
        setTotalCount(count || 0);
        if (searchTerm) {
          console.log(`[Logs] WhatsApp search "${searchTerm}": loaded ${allData.length} logs, filtered to ${allData.filter(l => JSON.stringify(l).toLowerCase().includes(searchTerm.toLowerCase())).length} matches`);
        }
      } catch (err) {
        console.error('Error loading WhatsApp logs:', err);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);
      let query = supabase
        .from('action_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (sourceFilter !== 'all') {
        query = query.eq('source', sourceFilter);
      }
      if (actorFilter !== 'all') {
        query = query.eq('actor_type', actorFilter);
      }
      if (actionFilter !== 'all') {
        query = query.eq('action', actionFilter);
      }

      // When searching, load more results to filter client-side
      // (details is JSONB and can't be easily searched server-side)
      const limit = searchTerm ? 500 : ITEMS_PER_PAGE;
      const offset = searchTerm ? 0 : page * ITEMS_PER_PAGE;
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;
      if (error) throw error;
      const allData = (data as unknown as ActionLog[]) || [];
      setLogs(allData);
      setTotalCount(count || 0);

      // Enrich: look up creative (video_name) + release name for entity_id refs.
      // Edge functions log entity_id = creative id but don't store the creative/
      // release name in details, so we resolve them here for display.
      try {
        const creativeIds = Array.from(new Set(
          allData.filter(l => l.entity_type === 'creative' && l.entity_id).map(l => l.entity_id as string)
        ));
        if (creativeIds.length === 0) {
          setCreativeMap({});
        } else {
          const { data: creatives } = await supabase
            .from('ad_creatives')
            .select('id, video_name, release_id')
            .in('id', creativeIds);
          const releaseIds = Array.from(new Set(
            (creatives || []).map(c => c.release_id).filter(Boolean) as string[]
          ));
          const releaseNames: Record<string, string> = {};
          if (releaseIds.length > 0) {
            const { data: rels } = await supabase
              .from('releases')
              .select('id, name')
              .in('id', releaseIds);
            (rels || []).forEach(r => { releaseNames[r.id] = r.name; });
          }
          const map: Record<string, { video_name: string | null; release_name: string | null }> = {};
          (creatives || []).forEach(c => {
            map[String(c.id)] = {
              video_name: c.video_name,
              release_name: c.release_id ? (releaseNames[c.release_id] || null) : null,
            };
          });
          setCreativeMap(map);
        }
      } catch (enrichErr) {
        console.warn('[Logs] creative/release enrichment failed:', enrichErr);
      }
      if (searchTerm) {
        const matches = allData.filter(l => {
          const term = searchTerm.toLowerCase();
          return l.action.toLowerCase().includes(term) ||
            l.entity_type.toLowerCase().includes(term) ||
            l.actor_name?.toLowerCase().includes(term) ||
            l.platform?.toLowerCase().includes(term) ||
            JSON.stringify(l.details).toLowerCase().includes(term);
        });
        console.log(`[Logs] Action search "${searchTerm}": loaded ${allData.length} logs, filtered to ${matches.length} matches`);
        if (matches.length === 0) {
          // Log sample to help debug
          console.log(`[Logs] Sample actions in loaded data:`, [...new Set(allData.map(l => l.action))]);
          console.log(`[Logs] Sample artist_names in loaded data:`, [...new Set(allData.map(l => (l.details as any)?.artist_name).filter(Boolean))].slice(0, 20));
        }
      }
    } catch (err) {
      console.error('Error loading action logs:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, statusFilter, sourceFilter, actorFilter, actionFilter, whatsappStatusFilter, whatsappTypeFilter, searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const switchTab = (tab: 'actions' | 'whatsapp') => {
    setActiveTab(tab);
    setPage(0);
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setActorFilter('all');
    setActionFilter('add_creative');
    setWhatsappStatusFilter('all');
    setWhatsappTypeFilter('all');
    setExpandedRows(new Set());
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.action.toLowerCase().includes(term) ||
      log.entity_type.toLowerCase().includes(term) ||
      log.actor_name?.toLowerCase().includes(term) ||
      log.platform?.toLowerCase().includes(term) ||
      JSON.stringify(log.details).toLowerCase().includes(term)
    );
  });

  const filteredWhatsappLogs = whatsappLogs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.message.toLowerCase().includes(term) ||
      log.type.toLowerCase().includes(term) ||
      log.error?.toLowerCase().includes(term) ||
      JSON.stringify(log.metadata || {}).toLowerCase().includes(term)
    );
  });

  const getWhatsappTypeColor = (type: string) => {
    switch (type) {
      case 'feedback':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'submission':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'ad_creative_submission':
        return 'bg-pink-500/10 text-pink-400 border-pink-500/20';
      case 'whatsapp_message':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0" />;
      case 'skipped':
        return <Clock className="h-4 w-4 text-yellow-500 dark:text-yellow-400 flex-shrink-0" />;
      default:
        return <Eye className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />;
    }
  };

  const getActorIcon = (actorType: string) => {
    switch (actorType) {
      case 'user':
        return <User className="h-3.5 w-3.5" />;
      case 'artist':
        return <User className="h-3.5 w-3.5 text-purple-400" />;
      case 'system':
        return <Bot className="h-3.5 w-3.5 text-gray-400" />;
      case 'cron':
        return <Zap className="h-3.5 w-3.5 text-orange-400" />;
      default:
        return <Bot className="h-3.5 w-3.5" />;
    }
  };

  const getSourceBadge = (source: string) => {
    if (source === 'auto') {
      return (
        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
          AUTO
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
        MANUAL
      </span>
    );
  };

  const getPlatformBadge = (platform: string | null) => {
    if (!platform) return null;
    const colors: Record<string, string> = {
      meta: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      tiktok: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
      whatsapp: 'bg-green-500/10 text-green-400 border-green-500/20',
      instagram: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    };
    const cls = colors[platform] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    return (
      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${cls}`}>
        {platform.toUpperCase()}
      </span>
    );
  };

  const formatTime = (iso: string) => {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFullTime = (iso: string) => {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const clearFilters = () => {
    setStatusFilter('all');
    setSourceFilter('all');
    setActorFilter('all');
    setActionFilter('all');
    setWhatsappStatusFilter('all');
    setWhatsappTypeFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const hasActiveFilters =
    statusFilter !== 'all' || sourceFilter !== 'all' || actorFilter !== 'all' ||
    actionFilter !== 'all' || whatsappStatusFilter !== 'all' || whatsappTypeFilter !== 'all' || searchTerm;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Logs</h1>
          <p className="text-sm text-gray-400 mt-1">
            {totalCount.toLocaleString()} total {activeTab === 'whatsapp' ? 'WhatsApp logs' : 'actions'}
          </p>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-800">
        <button
          onClick={() => switchTab('actions')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'actions'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <ScrollText className="h-4 w-4" />
          Action Logs
        </button>
        <button
          onClick={() => switchTab('whatsapp')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'whatsapp'
              ? 'border-green-500 text-white'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          WhatsApp Logs
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder={activeTab === 'whatsapp' ? 'Search messages, types, errors...' : 'Search actions, entities, details...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
          />
        </div>

        {activeTab === 'actions' && (
          <>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Actions</option>
              <option value="add_creative">Add Creative</option>
              <option value="sync_creative">Sync Creative</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="skipped">Skipped</option>
              <option value="info">Info</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Sources</option>
              <option value="manual">Manual</option>
              <option value="auto">Automated</option>
            </select>

            <select
              value={actorFilter}
              onChange={(e) => { setActorFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Actors</option>
              <option value="user">Users (Admin)</option>
              <option value="artist">Artists</option>
              <option value="system">System</option>
              <option value="cron">Cron</option>
            </select>
          </>
        )}

        {activeTab === 'whatsapp' && (
          <>
            <select
              value={whatsappStatusFilter}
              onChange={(e) => { setWhatsappStatusFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="info">Info</option>
            </select>

            <select
              value={whatsappTypeFilter}
              onChange={(e) => { setWhatsappTypeFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-gray-500"
            >
              <option value="all">All Types</option>
              <option value="feedback">Feedback</option>
              <option value="submission">Submission</option>
              <option value="ad_creative_submission">Ad Creative Submission</option>
              <option value="whatsapp_message">WhatsApp Message</option>
              <option value="notification">Notification</option>
            </select>
          </>
        )}

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-2 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      {/* Logs table */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden">
        {loading && (activeTab === 'whatsapp' ? whatsappLogs.length === 0 : logs.length === 0) ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-6 w-6 text-gray-500 animate-spin" />
          </div>
        ) : activeTab === 'whatsapp' ? (
          filteredWhatsappLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <Filter className="h-8 w-8 mb-2 opacity-50" />
              <p>No WhatsApp logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filteredWhatsappLogs.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                const hasDetails = (log.metadata && Object.keys(log.metadata).length > 0) || !!log.error;
                return (
                  <div
                    key={log.id}
                    className={`hover:bg-gray-800/30 transition-colors ${log.status === 'error' ? 'bg-red-500/5' : ''}`}
                  >
                    <div
                      className="flex items-center gap-3 px-4 py-2.5 cursor-pointer"
                      onClick={() => hasDetails && toggleRow(log.id)}
                    >
                      <div className="w-4 flex-shrink-0">
                        {hasDetails ? (
                          isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                          )
                        ) : null}
                      </div>

                      {getStatusIcon(log.status)}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-gray-300 truncate">
                            {log.message}
                          </span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${getWhatsappTypeColor(log.type)}`}>
                            {log.type.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 flex-shrink-0 w-36 text-right" title={formatFullTime(log.created_at)}>
                        {formatTime(log.created_at)}
                      </div>
                    </div>

                    {isExpanded && hasDetails && (
                      <div className="px-12 pb-3 pt-1 space-y-2">
                        {log.error && (
                          <div className="text-xs text-red-400">{log.error}</div>
                        )}
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <pre className="text-xs text-gray-400 bg-gray-950/50 rounded p-3 overflow-x-auto font-mono">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Filter className="h-8 w-8 mb-2 opacity-50" />
            <p>No logs found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {filteredLogs.map((log) => {
              const isExpanded = expandedRows.has(log.id);
              const hasDetails = log.details && Object.keys(log.details).length > 0;
              const creative = log.entity_type === 'creative' ? creativeMap[log.entity_id ?? ''] : undefined;
              const releaseName = (typeof log.details?.release_name === 'string' ? log.details.release_name : creative?.release_name) || null;
              return (
                <div
                  key={log.id}
                  className={`hover:bg-gray-800/30 transition-colors ${log.status === 'error' ? 'bg-red-500/5' : ''}`}
                >
                  <div
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer"
                    onClick={() => hasDetails && toggleRow(log.id)}
                  >
                    {/* Expand arrow */}
                    <div className="w-4 flex-shrink-0">
                      {hasDetails ? (
                        isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                        )
                      ) : null}
                    </div>

                    {/* Status icon */}
                    {getStatusIcon(log.status)}

                    {/* Action + entity */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white truncate">
                          {log.action}
                        </span>
                        {getPlatformBadge(log.platform)}
                        {getSourceBadge(log.source)}
                        {typeof log.details?.artist_name === 'string' && (
                          <span className="text-xs text-gray-400 truncate max-w-[120px]">
                            {log.details.artist_name as string}
                          </span>
                        )}
                        {creative?.video_name && (
                          <span className="text-xs text-gray-300 truncate max-w-[160px]" title={creative.video_name}>
                            {creative.video_name}
                          </span>
                        )}
                        {releaseName && (
                          <span className="text-xs text-gray-500 truncate max-w-[140px]" title={releaseName}>
                            {releaseName}
                          </span>
                        )}
                        {log.entity_type && !log.details?.artist_name && !creative?.video_name && (
                          <span className="text-xs text-gray-500">
                            {log.entity_type}
                            {log.entity_id ? `:${log.entity_id.slice(0, 8)}` : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actor */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0">
                      {getActorIcon(log.actor_type)}
                      <span className="max-w-[120px] truncate">
                        {log.actor_name || log.actor_type}
                      </span>
                    </div>

                    {/* Timestamp */}
                    <div className="text-xs text-gray-500 flex-shrink-0 w-36 text-right" title={formatFullTime(log.created_at)}>
                      {formatTime(log.created_at)}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && hasDetails && (
                    <div className="px-12 pb-3 pt-1">
                      <pre className="text-xs text-gray-400 bg-gray-950/50 rounded p-3 overflow-x-auto font-mono">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination (hidden during search — search loads all results at once) */}
      {totalPages > 1 && !searchTerm && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-400">
            Page {page + 1} of {totalPages} ({totalCount.toLocaleString()} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded disabled:opacity-30"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded disabled:opacity-30"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
